import { create } from 'zustand'
import type { BackgroundJob } from '@/services/jobs'

export interface ServerJobRecord {
  job: BackgroundJob
  /** First time this job id was registered (stable for sort) */
  createdAt: number
}

export interface SyntheticJobRecord {
  id: string
  title: string
  status: 'running' | 'success' | 'error'
  message?: string
  error?: string
  createdAt: number
}

interface BackgroundJobsState {
  serverById: Record<number, ServerJobRecord>
  synthetics: SyntheticJobRecord[]

  /** Upsert server job; preserves createdAt if job id already exists */
  registerServerJob: (job: BackgroundJob) => void
  removeServerJob: (jobId: number) => void

  startSynthetic: (title: string) => string
  completeSynthetic: (id: string, message: string) => void
  failSynthetic: (id: string, error: string) => void
  removeSynthetic: (id: string) => void
}

export const useBackgroundJobsStore = create<BackgroundJobsState>((set) => ({
  serverById: {},
  synthetics: [],

  registerServerJob: (job) =>
    set((state) => {
      const prev = state.serverById[job.id]
      return {
        serverById: {
          ...state.serverById,
          [job.id]: {
            job,
            createdAt: prev?.createdAt ?? Date.now(),
          },
        },
      }
    }),

  removeServerJob: (jobId) =>
    set((state) => {
      const next = { ...state.serverById }
      delete next[jobId]
      return { serverById: next }
    }),

  startSynthetic: (title) => {
    const id = `syn-${crypto.randomUUID()}`
    set((state) => ({
      synthetics: [
        ...state.synthetics,
        {
          id,
          title,
          status: 'running',
          createdAt: Date.now(),
        },
      ],
    }))
    return id
  },

  completeSynthetic: (id, message) =>
    set((state) => ({
      synthetics: state.synthetics.map((s) =>
        s.id === id ? { ...s, status: 'success' as const, message } : s
      ),
    })),

  failSynthetic: (id, error) =>
    set((state) => ({
      synthetics: state.synthetics.map((s) =>
        s.id === id ? { ...s, status: 'error' as const, error } : s
      ),
    })),

  removeSynthetic: (id) =>
    set((state) => ({
      synthetics: state.synthetics.filter((s) => s.id !== id),
    })),
}))

export function getServerJobsList(state: BackgroundJobsState): ServerJobRecord[] {
  return Object.values(state.serverById)
}
